import { useCallback, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import type { BondDocument } from "@/lib/brokerBonds";
import { bondDocumentGroups } from "@/lib/bondSubmissionProfile";
import type { BondRenderField } from "@/lib/bondFields";
import { formatFileSize } from "@/lib/brokerDocumentTypes";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { saveFile } from "@/lib/downloadFile";
import { formatListDate } from "@/lib/listFormat";
import { messageFor } from "@/lib/errors";

// BRK-S146 — the documents a broker attached during intake, read back on the
// Submission Profile.
//
// READ-ONLY, always. The profile is what a broker opens to see what they filed;
// attaching and removing belong to the wizard, where the configuration asked for
// them (BRK-S145). So there is no drop zone here and no Remove — not because a
// filed order forbids it, but because this surface is a record.
//
// ── THE DOWNLOAD GOES THROUGH THE ACCOUNT ────────────────────────────────────
//
// There is deliberately no download route on the ORDER: an intake document
// belongs to the insured's account (the order is the occasion), so the bytes
// come from `GET /api/broker/accounts/{id}/documents/{docId}/download`. That is
// the same arrangement the signed contract already uses (BRK-S141), and the
// reason `accountId` is a prop rather than something this component digs out.

export function BondDocumentsSection({
  accountId,
  /** The order's configuration fields, for the group headings. An empty list is
   *  legitimate — a failed config read still lets every document render, under
   *  a humanised key. */
  fields,
  /** Read by the PAGE, so the bid-result panel renders from the same list —
   *  `null` while loading or after a failed read. */
  documents,
  error,
}: {
  accountId: string;
  fields: readonly BondRenderField[];
  documents: BondDocument[] | null;
  error: string | null;
}) {
  const { authedRequest, authedBlob } = useAuth();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // For the TYPE labels. PMS's vocabulary, never a map in this bundle.
  const { labelFor } = useDocumentTypes(authedRequest);

  const download = useCallback(
    async (document: BondDocument) => {
      setBusyId(document.documentId);
      setDownloadError(null);
      try {
        // The SERVER names the file; `filename` is only the fallback, for a
        // response that carried no `Content-Disposition`.
        saveFile(
          await downloadAccountDocument(
            authedBlob,
            accountId,
            document.documentId,
          ),
          document.filename,
        );
      } catch (err) {
        // A 502 means the row is live and the object is missing — "retry" and
        // "we have a bug" are different answers and PMS distinguishes them
        // (PMS-S304), so its sentence is the one to show.
        setDownloadError(messageFor(err));
      } finally {
        setBusyId(null);
      }
    },
    [authedBlob, accountId],
  );

  const body = () => {
    if (error !== null) {
      return (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      );
    }
    if (documents === null) {
      return (
        <p role="status" className="text-sm text-brk-dim">
          Loading documents…
        </p>
      );
    }
    if (documents.length === 0) {
      // In words, and inside a bordered panel. An empty region on a record
      // cannot be told from a rendering bug (the BRK-S138 argument) — and here
      // "nothing was attached" is a real answer a broker may need to act on.
      return (
        <div className="rounded-lg border border-dashed border-brk-border px-4 py-6 text-center text-sm text-brk-dim">
          No documents were attached to this request.
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-5">
        {bondDocumentGroups(documents, fields, labelFor).map((group) => (
          <div key={group.fieldKey} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-brk-text">
              {group.label}
            </h3>
            <ul className="flex flex-col gap-2">
              {group.documents.map((document) => (
                <li
                  key={document.documentId}
                  className="flex flex-col gap-2 rounded-lg border border-brk-border bg-brk-panel p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-brk-text">
                      {document.filename}
                    </span>
                    <span className="truncate text-sm text-brk-dim">
                      {labelFor(document.type)} ·{" "}
                      {formatFileSize(document.byteSize)} ·{" "}
                      {formatListDate(document.uploadedAt)}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => void download(document)}
                    disabled={busyId === document.documentId}
                    className="shrink-0 self-start rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
                  >
                    {busyId === document.documentId ? "Preparing…" : "Download"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {/* Once, below the whole list rather than per row: a failed download is
            about the click that just happened. */}
        {downloadError !== null && (
          <p role="alert" className="text-sm text-brk-danger">
            {downloadError}
          </p>
        )}
      </div>
    );
  };

  return (
    <section
      aria-labelledby="section-documents"
      className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
    >
      <h2
        id="section-documents"
        className="text-lg font-semibold text-brk-text"
      >
        Documents
      </h2>
      {body()}
    </section>
  );
}
